// Intent: in-app "Regulamin i zasady" — the human-readable core of the terms
// (full legal drafts live in pol_transfers/docs/app/*, pending lawyer review;
// they'll be published on polfinance.pl after approval). Honest, short, PL.

import { router } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { ScreenHeader } from '@/components/ui/screen-header';
import { colors, radius, spacing } from '@/theme/tokens';

const SECTIONS: { icon: keyof typeof Ionicons.glyphMap; titleKey: string; bodyKey: string }[] = [
  { icon: 'gift-outline', titleKey: 'legal.freeTitle', bodyKey: 'legal.freeBody' },
  { icon: 'phone-portrait-outline', titleKey: 'legal.localTitle', bodyKey: 'legal.localBody' },
  { icon: 'cloud-upload-outline', titleKey: 'legal.cloudTitle', bodyKey: 'legal.cloudBody' },
  { icon: 'lock-closed-outline', titleKey: 'legal.vaultTitle', bodyKey: 'legal.vaultBody' },
  { icon: 'sparkles-outline', titleKey: 'legal.scanTitle', bodyKey: 'legal.scanBody' },
  { icon: 'people-outline', titleKey: 'legal.referralTitle', bodyKey: 'legal.referralBody' },
  { icon: 'school-outline', titleKey: 'legal.eduTitle', bodyKey: 'legal.eduBody' },
  { icon: 'mail-outline', titleKey: 'legal.complaintsTitle', bodyKey: 'legal.complaintsBody' },
];

export default function LegalScreen() {
  const { t } = useTranslation();
  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('legal.title')} />
        <AppText variant="bodyMd">{t('legal.intro')}</AppText>

        {SECTIONS.map(({ icon, titleKey, bodyKey }) => (
          <Card key={titleKey} style={{ flexDirection: 'row', gap: spacing.sm }}>
            <Ionicons name={icon} size={22} color={colors.primary} style={{ marginTop: 2 }} />
            <View style={{ flex: 1, gap: 2 }}>
              <AppText variant="labelLg" color={colors.navy}>
                {t(titleKey)}
              </AppText>
              <AppText variant="bodyMd">{t(bodyKey)}</AppText>
            </View>
          </Card>
        ))}

        {/* Full formal documents live in-app (no WordPress) — one tap away. */}
        {(['regulamin', 'polityka'] as const).map((doc) => (
          <Pressable
            key={doc}
            accessibilityRole="button"
            onPress={() => router.push(`/legal-doc?doc=${doc}`)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: spacing.sm,
              backgroundColor: colors.card,
              borderRadius: radius.card,
              padding: spacing.md,
            }}
          >
            <Ionicons name="document-text-outline" size={22} color={colors.navy} />
            <AppText variant="labelLg" color={colors.navy} style={{ flex: 1 }}>
              {doc === 'regulamin' ? 'Pełny regulamin' : 'Polityka prywatności'}
            </AppText>
            <Ionicons name="chevron-forward" size={18} color={colors.outline} />
          </Pressable>
        ))}

        <AppText variant="labelMd" color={colors.outline} style={{ textAlign: 'center' }}>
          {t('legal.footer')}
        </AppText>
      </ScrollView>
    </SafeAreaView>
  );
}
